import { Injectable, computed, effect, inject, signal, untracked } from '@angular/core';

import type { DsTableRow, DsTableSort } from '@gde-mit/angular-design-system';

import type { DocumentLibraryVisibilityFilter } from '@shared/constants/document-library-visibility-filter.constants';

import { DocumentLibraryDeleteService } from '@shared/services/document-library/document-library-delete.service';
import { DocumentLibraryListQueryStore } from '@shared/services/document-library/document-library-list-query.store';
import { DocumentLibraryVisibilityService } from '@shared/services/document-library/document-library-visibility.service';

import { documentLibraryCellText, isOwnDocumentRow } from '@shared/utils/document-library-row.util';

import type { DocumentVisibility } from '@api';

// Route-scoped: provided on the document-library route, not application-wide.
// eslint-disable-next-line @angular-eslint/use-injectable-provided-in -- route-scoped store, provided via route `providers`
@Injectable()
export class DocumentLibraryStore {
  readonly #query = inject(DocumentLibraryListQueryStore);
  readonly #deleteService = inject(DocumentLibraryDeleteService);
  readonly #visibilityService = inject(DocumentLibraryVisibilityService);

  readonly #selection = signal<DsTableRow[]>([]);

  readonly selectedCount = computed(() => this.#selection().length);

  readonly initialLoading = this.#query.initialLoading;
  readonly tableLoading = this.#query.tableLoading;
  readonly initialLoadDone = this.#query.initialLoadDone;
  readonly searchInput = this.#query.searchInput;
  readonly typeFilter = this.#query.typeFilter;
  readonly visibilityFilter = this.#query.visibilityFilter;
  readonly pageIndex = this.#query.pageIndex;
  readonly pageSize = this.#query.pageSize;
  readonly totalCount = this.#query.totalCount;
  readonly tableRows = this.#query.tableRows;
  readonly tableSort = this.#query.tableSort;
  readonly loadError = this.#query.loadError;
  readonly deletingIds = this.#deleteService.deletingIds;
  readonly updatingVisibilityIds = this.#visibilityService.updatingIds;
  readonly selection = this.#selection.asReadonly();

  constructor() {
    effect(() => {
      this.#query.searchInput();
      this.#query.typeFilter();
      this.#query.visibilityFilter();
      this.#query.pageIndex();
      this.#query.pageSize();
      untracked(() => this.clearSelection());
    });
  }

  loadList(): Promise<void> {
    return this.#query.loadList();
  }

  onSearchInput(value: string): void {
    this.clearSelection();
    this.#query.onSearchInput(value);
  }

  onTypeFilterChange(fileType: string): void {
    this.clearSelection();
    this.#query.onTypeFilterChange(fileType);
  }

  onVisibilityFilterChange(filter: DocumentLibraryVisibilityFilter): void {
    this.clearSelection();
    this.#query.onVisibilityFilterChange(filter);
  }

  onPageChanged(index: number): void {
    this.clearSelection();
    this.#query.onPageChanged(index);
  }

  onPageSizeChanged(size: number): void {
    this.clearSelection();
    this.#query.onPageSizeChanged(size);
  }

  onTableSortChange(sort: DsTableSort[]): void {
    this.#query.onTableSortChange(sort);
  }

  onSelectionChange(rows: DsTableRow[]): void {
    this.#selection.set(rows.filter((row) => isOwnDocumentRow(row)));
  }

  clearSelection(): void {
    this.#selection.set([]);
  }

  onVisibilityChange(row: DsTableRow, visibility: string): void {
    const id = documentLibraryCellText(row['id']);
    const current = documentLibraryCellText(row['visibility']);
    if (!id || visibility === current) {
      return;
    }

    void this.#visibilityService.updateVisibility({
      id,
      visibility: visibility as DocumentVisibility,
      onUpdated: () => this.loadList(),
    });
  }

  bulkApplyVisibility(visibility: DocumentVisibility): void {
    const updates = this.#selection()
      .map((row) => ({
        id: documentLibraryCellText(row['id']),
        visibility,
        current: documentLibraryCellText(row['visibility']),
      }))
      .filter(({ id, current }) => id && current !== visibility)
      .map(({ id, visibility: nextVisibility }) => ({ id, visibility: nextVisibility }));

    if (updates.length === 0) {
      return;
    }

    void this.#visibilityService.updateManyVisibility({
      updates,
      onUpdated: async () => {
        this.clearSelection();
        await this.loadList();
      },
    });
  }

  bulkDeleteSelected(): void {
    const items = this.#selection()
      .filter((row) => isOwnDocumentRow(row))
      .map((row) => ({
        id: documentLibraryCellText(row['id']),
        fileName: documentLibraryCellText(row['fileName']),
      }))
      .filter(({ id }) => id.length > 0);

    if (items.length === 0) {
      return;
    }

    void this.#deleteService.deleteManyWithConfirmation({
      items,
      onDeleted: async () => {
        this.clearSelection();
        await this.loadList();
      },
    });
  }

  deleteRow(row: DsTableRow): void {
    if (!isOwnDocumentRow(row)) {
      return;
    }

    const id = documentLibraryCellText(row['id']);
    const fileName = documentLibraryCellText(row['fileName']);
    if (!id) {
      return;
    }

    void this.#deleteService.deleteWithConfirmation({
      id,
      fileName,
      onDeleted: () => this.loadList(),
    });
  }
}
